/**
 * TAG/NACHT-UMSCHALTER (Rafael, 03.10.2026): heller oder dunkler Modus der CUI.
 *
 * Geraete-Zustand, kein Server-Zustand: gemerkt in localStorage, wirkt sofort ohne Neuladen.
 * Drei Stellungen: hell, dunkel, auto (folgt dem Geraet). Vorgabe ohne eigene Wahl: dunkel, fuer alle (Rafael, 05.10.2026). Umgesetzt als Attribut `data-theme` an <html>; die helle Palette
 * ueberschreibt in styles/tokyo-night.css dieselben --tn-*-Variablen, die die Oberflaeche
 * ohnehin benutzt. Fest eingetragene Farben folgen dem Umschalter NICHT — sie muessen auf
 * Variablen umgestellt werden (oder auf die Paare in --cui-flaeche-*).
 */
import { useSyncExternalStore } from 'react';
import { beobachteBuehnenSeiten, reicheAnBuehnenSeiten } from './buehnenSeitenFarbschema';

/** Was tatsaechlich gezeichnet wird. */
export type Farbschema = 'dunkel' | 'hell';
/**
 * Was gewaehlt ist. „auto" folgt der Hell/Dunkel-Einstellung des Geraets
 * (prefers-color-scheme, live — Rafael, 03.10.2026 14:04). Ohne gespeicherte Wahl: vorgabe().
 */
export type FarbschemaWahl = Farbschema | 'auto';
export const FARBSCHEMA_KEY = 'cui-farbschema-v1';
/** Reihum: ☀ → ☾ → Auto → ☀. */
export const NAECHSTE_WAHL: Record<FarbschemaWahl, FarbschemaWahl> = { hell: 'dunkel', dunkel: 'auto', auto: 'hell' };

const GERAET_HELL = '(prefers-color-scheme: light)';

/**
 * VORGABE NACH ROLLE (Rafael, 03.10.2026 14:26): Partner (Nicht-Admins) ohne eigene Wahl
 * starten mit „auto" (wie ihr Geraet), Admins ohne eigene Wahl dunkel. Eine gespeicherte
 * Wahl gilt immer. Die Rolle ist beim ersten Zeichnen (main.tsx) noch nicht geladen —
 * deshalb merkt sich das Geraet die zuletzt gesehene Rolle hier; nur beim allerersten
 * Laden auf einem Geraet schaltet die Vorgabe einmal um, sobald der Nutzer bekannt ist.
 */
export const ROLLE_KEY = 'cui-farbschema-rolle-v1';

/* ABGELOEST 05.10.2026 (Rafael, als Mo angemeldet: „die weisse ansicht ist teilweise irritierend —
 * standardmaessig die dunkle ansicht bitte"): auch Partner starten dunkel. Wer selbst hell oder
 * Auto waehlt, behaelt das. Die Rolle wird weiter gemerkt, entscheidet aber nichts mehr. */
function vorgabe(): FarbschemaWahl {
  return 'dunkel';
}

function gespeicherteWahl(): FarbschemaWahl | null {
  try {
    const v = localStorage.getItem(FARBSCHEMA_KEY);
    return v === 'hell' || v === 'dunkel' || v === 'auto' ? v : null;
  } catch { return null; }
}

function liesWahl(): FarbschemaWahl {
  return gespeicherteWahl() ?? vorgabe();
}

function geraetIstHell(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(GERAET_HELL).matches;
}

export function wirksamesSchema(wahl: FarbschemaWahl): Farbschema {
  return wahl === 'auto' ? (geraetIstHell() ? 'hell' : 'dunkel') : wahl;
}

let wahl: FarbschemaWahl = liesWahl();
const hoerer = new Set<() => void>();

/** Setzt das Attribut an <html> nach der aktuellen Wahl (bei „auto" nach dem Geraet). */
export function wendeFarbschemaAn(w: FarbschemaWahl = wahl) {
  const schema = wirksamesSchema(w);
  const wurzel = document.documentElement;
  wurzel.dataset.theme = schema === 'hell' ? 'light' : 'dark';
  wurzel.dataset.themeWahl = w;
  wurzel.style.colorScheme = schema === 'hell' ? 'light' : 'dark';
}

/** Oberflaeche und Buehnenseiten (lib/buehnenSeitenFarbschema.ts) ueber das gezeichnete Schema informieren. */
function melde() { reicheAnBuehnenSeiten(wirksamesSchema(wahl)); hoerer.forEach(fn => fn()); }

export function setzeFarbschema(w: FarbschemaWahl) {
  wahl = w;
  try { localStorage.setItem(FARBSCHEMA_KEY, w); } catch (err) { console.warn('[farbschema] nicht gespeichert, gilt nur bis zum Neuladen:', err); }
  wendeFarbschemaAn(w);
  melde();
}

if (typeof window !== 'undefined') {
  /* Ein zweiter Reiter im selben Browser schaltet mit. */
  window.addEventListener('storage', e => {
    if (e.key !== FARBSCHEMA_KEY && e.key !== null) return;
    wahl = liesWahl(); wendeFarbschemaAn(wahl); melde();
  });
  /* „Auto": stellt das Geraet um (z. B. abends), schaltet die CUI live mit. */
  if (typeof window.matchMedia === 'function') {
    window.matchMedia(GERAET_HELL).addEventListener('change', () => {
      if (wahl !== 'auto') return;
      wendeFarbschemaAn(wahl); melde();
    });
  }
  /* Die Seiten der Buehnen laufen abgeschottet und hoeren auf Zuruf (lib/buehnenSeitenFarbschema.ts). */
  beobachteBuehnenSeiten(() => wirksamesSchema(wahl));
}

/** Vom AuthContext gerufen, sobald der Nutzer geladen ist. Schreibt nur die Rolle, nie die Wahl. */
export function merkeRolleFuerFarbschema(rolle: string | undefined | null) {
  if (!rolle) return;
  const art = rolle === 'admin' ? 'admin' : 'partner';
  try { if (localStorage.getItem(ROLLE_KEY) !== art) localStorage.setItem(ROLLE_KEY, art); } catch { /* Vorgabe gilt dann nur bis zum Neuladen */ }
  if (gespeicherteWahl() !== null) return;
  const neu = vorgabe();
  if (neu === wahl) return;
  wahl = neu; wendeFarbschemaAn(wahl); melde();
}

let schnappschuss = { wahl, schema: wirksamesSchema(wahl) };
function lieferSchnappschuss() {
  const schema = wirksamesSchema(wahl);
  if (schnappschuss.wahl !== wahl || schnappschuss.schema !== schema) schnappschuss = { wahl, schema };
  return schnappschuss;
}

/** Gewaehlte Stellung und das, was davon gerade gezeichnet wird. */
export function useFarbschemaWahl(): { wahl: FarbschemaWahl; schema: Farbschema } {
  return useSyncExternalStore(fn => { hoerer.add(fn); return () => { hoerer.delete(fn); }; }, lieferSchnappschuss, () => ({ wahl: 'dunkel' as const, schema: 'dunkel' as const }));
}

export function useFarbschema(): Farbschema {
  return useFarbschemaWahl().schema;
}

/**
 * Schriftfarbe aus einer Datenfarbe (z. B. Kontofarbe aus der Konfiguration), die als Hex
 * hereinkommt und deshalb keiner Variablen folgen kann: dunkel unveraendert, hell um
 * --tn-schrift-abdunkeln mit Schwarz gemischt, damit sie auf hellem Grund lesbar bleibt.
 */
export function schriftAufHell(farbe: string): string {
  return `color-mix(in srgb, ${farbe}, black var(--tn-schrift-abdunkeln, 0%))`;
}

/**
 * Farbe mit Alpha-Endung, wie frueher `${farbe}22`. Haelt das alte Verhalten im dunklen Modus
 * genau ein: war die Farbe frueher fest (#hex, jetzt var(--tnf-…)), entsteht dieselbe Toenung
 * per color-mix — die dem Hellmodus folgt. War sie schon vorher eine Variable oder rgb(),
 * war die Endung wirkungslos; das bleibt so (sonst saehe dunkel anders aus als bisher).
 */
export function mitAlpha(farbe: string, hh: string): string {
  if (farbe.startsWith('var(--tnf-')) return `color-mix(in srgb, ${farbe} ${Math.round(parseInt(hh, 16) / 2.55 * 10) / 10}%, transparent)`;
  return farbe + hh;
}
